import type { DashboardLeadRow } from "./dashboardLeads";
import { isFakeVerdict, leadQualityLabel } from "../components/clients/leadQuality";

const REASON_LABELS: Record<string, string> = {
  budget_4000_plus: "$4,000+ budget",
  budget_3000_plus: "$3,000+ budget",
  budget_2500_plus: "$2,500+ budget",
  budget_2000_plus: "$2,000+ budget",
  budget_1500_plus: "$1,500+ budget",
  budget_below_1500: "budget below $1,500",
  budget_missing: "budget missing",
  move_within_45_days: "moving within 45 days",
  move_within_90_days: "moving within 90 days",
  move_over_90_days: "moving after 90 days",
  move_date_past: "move date has passed",
  move_timing_missing_or_unstructured: "move timing unclear",
  priority_area_match: "Midtown/Buckhead preference",
  area_preferences_present: "area preferences provided",
  area_preferences_missing: "area preference missing",
  detailed_custom_request: "detailed renter request",
  specific_custom_request: "specific renter request",
  custom_request_present: "custom request provided",
  core_fields_complete: "complete contact and search details",
  // These reason codes are emitted by the scorer but had no labels, so their
  // chips rendered as blank slots (filter(Boolean) dropped them silently).
  core_fields_4_of_5: "most contact and search details",
  core_fields_3_of_5: "some contact and search details",
  core_fields_2_of_5: "few contact and search details",
  core_fields_1_of_5: "minimal contact and search details",
  core_fields_0_of_5: "contact and search details missing",
  apartment_size_present: "apartment size provided",
  pet_requirements_present: "pet requirements provided",
};

export function compareLeadOpportunity(
  a: DashboardLeadRow,
  b: DashboardLeadRow
): number {
  const scoreDelta =
    (b.revenue_opportunity_score ?? -1) -
    (a.revenue_opportunity_score ?? -1);
  if (scoreDelta !== 0) return scoreDelta;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

export function revenueOpportunityLabel(lead: DashboardLeadRow): string {
  const score = lead.revenue_opportunity_score;
  if (typeof score !== "number") return "Opportunity pending";
  if (lead.revenue_opportunity_band === "high") return `Top opportunity · ${score}`;
  if (lead.revenue_opportunity_band === "medium") return `Promising · ${score}`;
  return `Limited detail · ${score}`;
}

export function revenueOpportunityReasons(lead: DashboardLeadRow): string {
  const reasons = (lead.revenue_opportunity_reasons || [])
    .map((reason) => REASON_LABELS[reason])
    .filter(Boolean)
    .slice(0, 3);
  return reasons.length > 0
    ? reasons.join(" · ")
    : "Revenue opportunity uses stated budget, timing, market, and form detail.";
}

/**
 * Davis first-refusal window (PKT-0355, Grant's 2026-07-28 scope correction:
 * no auto-assignment — the window is VISIBILITY, not an auth gate).
 *
 * A high-band unclaimed lead is "Davis's first shot" for its first 2 hours
 * (Grant default, 2026-08-01 GO — change the constant when he rules
 * otherwise). Past the window, an unclaimed high-band lead has FALLEN
 * THROUGH and the feed must say so loudly instead of letting it blend in.
 * Non-high leads never enter the window.
 */
export const FIRST_SHOT_WINDOW_MS = 2 * 60 * 60 * 1000;

export type FirstShotState =
  | { phase: "window"; msLeft: number }
  | { phase: "fell_through"; msUnclaimed: number }
  | null;

export function firstShotState(
  lead: DashboardLeadRow,
  nowMs: number
): FirstShotState {
  if (lead.revenue_opportunity_band !== "high") return null;
  const createdMs = new Date(lead.created_at).getTime();
  if (!Number.isFinite(createdMs)) return null;
  const age = nowMs - createdMs;
  // Client clock behind the server: treat as a fresh window, never as fallen.
  if (age < 0) return { phase: "window", msLeft: FIRST_SHOT_WINDOW_MS };
  if (age < FIRST_SHOT_WINDOW_MS) {
    return { phase: "window", msLeft: FIRST_SHOT_WINDOW_MS - age };
  }
  return { phase: "fell_through", msUnclaimed: age };
}

function formatShortDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 1) return "under 1m";
  const days = Math.floor(totalMinutes / 1_440);
  if (days >= 2) return `${days}d`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 1) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}

export function firstShotLabel(state: FirstShotState): string | null {
  if (!state) return null;
  if (state.phase === "window") {
    return `Davis first shot · ${formatShortDuration(state.msLeft)} left`;
  }
  return `Fell through · unclaimed ${formatShortDuration(state.msUnclaimed)}`;
}

// Scorer v2: true when the persisted verdict is FAKE. Shared by the label
// and the chip color so the veto cannot drift apart. PKT-0944a: one reader.
export function hasFakeIntelTier(lead: DashboardLeadRow): boolean {
  return isFakeVerdict(lead);
}

// Chip color for the contact-confidence chip (NewLeadsFeed): the label band
// as reconciled with the verdict (leadQualityLabel).
export function contactConfidenceChipColor(
  lead: DashboardLeadRow
): "warning" | "default" {
  return leadQualityLabel(lead) === "likely_bad_lead" ? "warning" : "default";
}

export function contactConfidenceLabel(lead: DashboardLeadRow): string {
  if (
    lead.verification_state !== "completed" ||
    typeof lead.verification_score !== "number"
  ) {
    return "Contact pending";
  }

  if (!lead.verification_provider) {
    return `Contact basic screen · ${lead.verification_score}`;
  }
  // PKT-0944a: the band as reconciled with the verdict — a FAKE verdict
  // reads weak, UNCERTAIN never strong. Absent tier (pre-v2 rows) falls
  // through to the stored label unchanged.
  const band = leadQualityLabel(lead);
  if (band === "high_confidence") {
    return `Contact strong · ${lead.verification_score}`;
  }
  if (band === "needs_review") {
    return `Contact review · ${lead.verification_score}`;
  }
  return `Contact weak · ${lead.verification_score}`;
}
